import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button, Form, FormGroup, Input, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { LoadingIcon } from '../../../common/views/LoadingIcon';
import { useSendFeedbackMutation } from '../../../../redux/api/fymAPI';
import { MUSIC24_SUPPORT_EMAIL } from '../../../../utils/openSupportEmail';
import { useSupportEmail } from '../../../support/hooks';
import { supportMessages } from './supportMessages';
import { Panel, PanelBody, PanelHead } from '../../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from '../settingsCards.module.scss';
import styles from './support.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** `Joi.string().min(3)` on the server (POST /common/feedback). */
const MIN_LENGTH = 3;
const MAX_LENGTH = 10000;

interface Props {
  userEmail: string;
  deviceId: string | undefined;
}

/** "Write to us": a message to support, with the email link as a second way (design settings-redesign d1, d2). */
const SupportMessageCardComponent: React.FC<Props> = ({ userEmail, deviceId }) => {
  const intl = useIntl();
  const [message, setMessage] = useState('');
  const [sendFeedback, { isLoading, isSuccess, isError, reset }] = useSendFeedbackMutation();
  const openEmail = useSupportEmail(userEmail, deviceId);
  const canSend = !isLoading && message.trim().length >= MIN_LENGTH;

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      sendFeedback(message.trim())
        .unwrap()
        .then(() => setMessage(''))
        .catch(() => undefined);
    },
    [message, sendFeedback]
  );

  const emailLink = (
    <button type="button" className={cards.link} onClick={openEmail}>
      {MUSIC24_SUPPORT_EMAIL}
    </button>
  );

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(supportMessages.formTitle)}
        subtitle={
          <span data-clarity-mask="true">{intl.formatMessage(supportMessages.replyTo, { email: userEmail })}</span>
        }
      />
      {isSuccess ? (
        <PanelBody stack>
          <Alert color="success" className="mb-0 d-flex gap-2">
            <FontAwesomeIcon icon={faCheck} className="mt-1" />
            <span data-clarity-mask="true">
              <strong>{intl.formatMessage(supportMessages.sentTitle)}</strong>{' '}
              {intl.formatMessage(supportMessages.sentBody, { email: userEmail })}
            </span>
          </Alert>
          <div>
            <Button color="secondary" outline size="sm" onClick={reset}>
              {intl.formatMessage(supportMessages.writeAnother)}
            </Button>
          </div>
        </PanelBody>
      ) : (
        <PanelBody>
          <Form onSubmit={submit} className={cards.stack}>
            <FormGroup className="mb-0">
              <Label for="supportMessage">{intl.formatMessage(supportMessages.messageLabel)}</Label>
              <Input
                id="supportMessage"
                type="textarea"
                className={styles.message}
                maxLength={MAX_LENGTH}
                value={message}
                placeholder={intl.formatMessage(supportMessages.messagePlaceholder)}
                onChange={(event) => setMessage(event.target.value)}
                disabled={isLoading}
              />
            </FormGroup>
            {isError && (
              <Alert color="danger" className="mb-0">
                {intl.formatMessage(supportMessages.failed, { email: MUSIC24_SUPPORT_EMAIL })}
              </Alert>
            )}
            <div className="d-flex flex-wrap align-items-center gap-3">
              <Button type="submit" color="primary" disabled={!canSend}>
                {isLoading ? (
                  <>
                    <LoadingIcon small /> {intl.formatMessage(supportMessages.sending)}
                  </>
                ) : (
                  intl.formatMessage(supportMessages.send)
                )}
              </Button>
              <span className={styles.alt}>{intl.formatMessage(supportMessages.orEmail, { email: emailLink })}</span>
            </div>
          </Form>
        </PanelBody>
      )}
    </Panel>
  );
};

export const SupportMessageCard = TypedMemo(SupportMessageCardComponent);
